import { useEffect } from 'react'

import { useRequest } from 'ahooks'

import { KindDetail } from '@eastcoal/api/system/interface'
import { queryKindTreeApi } from '@eastcoal/api/system/kind/queryKindTree'

import { KeySet } from '@eastcoal/common/types/utilities'

import { useKindAllNodeStoreApi } from '../hooks/useStore'
import { KindAllNodeAction, KindAllNodeState } from '../store/interface'

import { KindAllNodeStoreUpdaterProps } from './interface'

const useStoreUpdater = <M, K extends KeySet<KindAllNodeState<M>>>(
  key: K,
  value: KindAllNodeState<M>[K]
) => {
  const { setState } = useKindAllNodeStoreApi()

  useEffect(() => {
    setState({ [key]: value })
  }, [key, value, setState])
}

const useStoreSetter = <M, K extends KeySet<KindAllNodeAction<M>>>(
  setter: K,
  value: Parameters<KindAllNodeAction<M>[K]>[0]
) => {
  const { getState } = useKindAllNodeStoreApi()

  useEffect(() => {
    getState()[setter](value as any)
  }, [setter, value, getState])
}

const EMPTY_ARRAY: KindDetail[] = []

export const KindAllNodeStoreUpdater = <M>({
  multiple,
  value,
  onChange
}: KindAllNodeStoreUpdaterProps<M>) => {
  const {
    data = EMPTY_ARRAY,
    error,
    loading,
    refreshAsync
  } = useRequest(queryKindTreeApi, {
    manual: false,
    defaultParams: [
      {
        enable: true
      }
    ]
  })

  useStoreUpdater('data', data)
  useStoreUpdater('error', error)
  useStoreUpdater('loading', loading)
  useStoreUpdater('reload', refreshAsync)

  useStoreUpdater('multiple', multiple)
  useStoreUpdater<M, 'onChange'>('onChange', onChange)

  useStoreSetter<M, 'setValue'>('setValue', value)

  return null
}
